import { lazy, Suspense, useState, useSyncExternalStore } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { AuthProvider, ProtectedRoute } from '@podus/auth';
import { authConfig } from './auth-config';
import { siteContentVersion } from './lib/siteContent';
import Preloader from '@components/preloader';
import { ToastProvider } from '@components/Toast';
import CookieConsent from '@components/landing/CookieConsent';
/**
 * Every page is loaded on demand.
 *
 * Statically imported, all eighteen landed in one chunk, so a reader arriving at
 * an article downloaded the whole landing page, the help centre and every
 * listing before React could draw anything. Split, each URL pays for its own
 * page. The cost of splitting is normally a second round trip, because the
 * browser cannot know which chunk it needs until the app has parsed and picked
 * the route; `api/html.js` removes that by reading the build manifest and
 * preloading the chunk for the URL it is serving, so the two arrive together.
 *
 * The paths below are also what `ROUTE_MODULE` in `api/_meta.js` maps, and the
 * build fails loudly rather than silently dropping a hint if the two drift.
 */
const Home = lazy(() => import('./pages/Home'));
const OurSolutions = lazy(() => import('./pages/OurSolutions'));
const About = lazy(() => import('./pages/About'));
const FocusArea = lazy(() => import('./pages/FocusArea'));
const Innovation = lazy(() => import('./pages/Innovation'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/Projects/Detail'));
const ResourcesBlog = lazy(() => import('./pages/Resources/Blog'));
const BlogArticle = lazy(() => import('./pages/Resources/BlogArticle'));
const Publications = lazy(() => import('./pages/Resources/Publications'));
const Infographics = lazy(() => import('./pages/Resources/Infographics'));
const Reports = lazy(() => import('./pages/Resources/Reports'));
const CourtRanking = lazy(() => import('./pages/Resources/CourtRanking'));
const Stories = lazy(() => import('./pages/Resources/Stories'));
const Story = lazy(() => import('./pages/Resources/Story'));
const Gallery = lazy(() => import('./pages/Resources/Gallery'));
const Help = lazy(() => import('./pages/Help'));
const NotFound = lazy(() => import('./pages/NotFound'));

/** Set once the intro has played, so returning visitors go straight to the page. */
export const INTRO_SEEN_KEY = 'cg-intro-seen';

/**
 * Whether this page load plays the intro. Resolved synchronously at import time —
 * before any component mounts — because Hero and Navbar read `__cgPreloading`
 * from their own mount effects to decide whether to wait for the hand-off event.
 * Leaving it unset (or stale) is what previously stranded the hero mid-reveal.
 */
const shouldPlayIntro = (() => {
    if (typeof window === 'undefined') return false;
    try {
        return !window.localStorage.getItem(INTRO_SEEN_KEY);
    } catch {
        // Storage blocked (private mode, cookie policy) — play it rather than fail.
        return true;
    }
})();

if (typeof window !== 'undefined') window.__cgPreloading = shouldPlayIntro;

export function App() {
    // The content pools are read during render. When the CMS answers after first
    // paint — a slow connection, a cold API — this re-renders the tree so every
    // page reads the real content instead of the bundled stand-ins it started on.
    useSyncExternalStore(siteContentVersion.subscribe, siteContentVersion.get);

    // First visit only. Repeat visits — and every in-app navigation back to Home —
    // render the page directly, with no loader and no waiting on the hand-off.
    const [isAppLoading, setIsAppLoading] = useState(shouldPlayIntro);

    const handleLoadingComplete = () => {
        setIsAppLoading(false);
    };

    return (
        <ToastProvider>
            <BrowserRouter>
                {isAppLoading && <Preloader onLoadingComplete={handleLoadingComplete} />}
                <AuthProvider config={authConfig}>
                    {/* Holds the viewport while a page's chunk arrives, so the
                        swap to the real page moves nothing that is on screen. */}
                    <Suspense fallback={<div className="min-h-dvh bg-white" />}>
                        <Routes>
                            {/* Landing page */}
                            <Route index element={<Home />} />

                            {/* Our Solutions detail page */}
                            <Route path="our-solutions" element={<OurSolutions />} />

                            {/* About Us page */}
                            <Route path="about" element={<About />} />

                            {/* Focus Area pages — one template, slug-driven content */}
                            <Route path="focus-areas/:slug" element={<FocusArea />} />

                            {/* Innovation (product) pages — one template, slug-driven content */}
                            <Route path="innovations/:slug" element={<Innovation />} />

                            {/* Resources */}
                            <Route path="projects" element={<Projects />} />
                            <Route path="projects/:slug" element={<ProjectDetail />} />

                            <Route path="resources/blog" element={<ResourcesBlog />} />
                            <Route path="resources/blog/:slug" element={<BlogArticle />} />
                            <Route path="resources/publications" element={<Publications />} />
                            <Route path="resources/infographics" element={<Infographics />} />
                            <Route path="resources/reports" element={<Reports />} />
                            <Route path="resources/stories" element={<Stories />} />
                            <Route path="resources/stories/:slug" element={<Story />} />
                            <Route path="resources/gallery" element={<Gallery />} />
                            <Route path="resources/court-ranking" element={<CourtRanking />} />

                            {/* Protected dashboard routes */}
                            <Route element={<ProtectedRoute />}>{/* Add protected routes here */}</Route>

                            {/* 404 */}
                            <Route path="help" element={<Help />} />

                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                    <CookieConsent />
                </AuthProvider>
            </BrowserRouter>
        </ToastProvider>
    );
}
